<template>
  <div class="relative w-full h-full overflow-hidden text-sm bg-gray-800 three-d-slide viewer-container lg_text-base">
    <div class="progress-container">
      <div class="progress" :style="{ '--progress-amount': progress, width: `${progress * 100}%`, opacity: progress < 1 ? 1 : 0 }" />
    </div>
    <div ref="container" class="absolute w-full h-full">
      <LoadingSpinner v-if="progress < 1" class="absolute z-10 top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 transform" size="sm" />
      <EditorModelEditorLabelsView
        disabled
        :labels-manager="threeDViewer.labelsManager"
        :is-model-tweening="animating"
        :always-slow-transition="user.deckUserType === 'controlled'"
        :loading-progress="progress"
        @resize="onLabelResize"
      />
    </div>
    <ClientOnly>
      <Portal to="three-d-error-modal">
        <VModal :showing="showErrorModal" hide-close class="pointer-events-auto">
          <div class="max-w-md text-sm text-center">
            <p class="mt-1 text-lg font-medium">Failed to load 3D model</p>
            <p class="text-gray-600 my-4">Please refresh your browser and try again.</p>
            <VButton variant="copper" class="_w-full" @click="onErrorModalRefresh">Refresh</VButton>
          </div>
        </VModal>
      </Portal>
    </ClientOnly>

    <ClientOnly>
      <Portal to="deck-controls-three-d-legends">
        <div v-if="selectedLegends.length" class="absolute bottom-20 right-4 flex w-full h-full justify-end items-end gap-8 p-8 pr-0">
          <DeckAssayLegend v-for="legend in numericalAssayLegends" :key="legend.id" :legend="legend as NumericalLegend" />
          <DeckCategoricalLegend v-for="legend in categoricalAssayLegends" :key="legend.id" :legend="legend as CategoricalLegend" />
        </div>
      </Portal>
    </ClientOnly>

    <ClientOnly>
      <Portal to="deck-controls-three-d-layer">
        <div v-if="shouldShowLayerList" class="relative pointer-events-auto">
          <DeckControlsContainer>
            <DeckControlButton class="p-2.5" :is-active="layerListExpanded" @click="layerListExpanded = !layerListExpanded">
              <LayerIcon />
            </DeckControlButton>
          </DeckControlsContainer>

          <EditorDeckSlidesThreeDSearchableLayerList
            v-show="layerListExpanded"
            class="absolute overflow-hidden text-white rounded-lg shadow-md h-96 w-88 bottom-24 -left-64"
            readonly
            :search="threeDViewer.search.value"
            :layer-groups="layerListGroupsWithLegends"
            :disabled="progress < 1"
            :three-d-viewer="threeDViewer"
            @set-search="threeDViewer.setSearch"
            @set-layer-visible="onSetLayerVisible"
          />
        </div>
        <DeckThreeDLayerList
          v-else-if="threeDViewer.layerGroups.value.length && progress === 1"
          v-model:expanded="layerListExpanded"
          :layer-groups="layerGroupsWithoutDrillLabels"
          @set-layer-visible="deprecatedOnSetLayerVisible"
        />
      </Portal>
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
import type { DeepReadonly, PropType } from 'vue'
import type { DeckUser, Slide } from '@/entities/deck'
import { getReadOnlyControls } from '@/lib/three-d-resources/controls/readonly-controls'
import { isDrillDataSetGroup } from '@/lib/three-d-editor/utils'
import { ControlEvent } from '@/lib/three-d-resources/controls/viewerControls'
import { debounce } from '@/lib/utils'
import { Clock } from 'three'
import type { NodeVisibility } from '@/entities/model3d'
import type { UseThreeDViewer } from '@/composables/useThreeDViewer'
import type { TextLabel } from '~/lib/three-d-resources/labels-manager'
import LayerIcon from '@/components/three-d/LayerIcon.svg'
import type { DrillDataSetLayerGroup } from '~/composables/useDrillDataSetGroups'
import type { NumericalLegend, CategoricalLegend } from '~/entities/drillHole'
import { isDrillLabelVisibility } from '@/entities/model3d'

const props = defineProps({
  slide: {
    type: Object as PropType<DeepReadonly<Slide>>,
    required: true,
  },
  user: {
    type: Object as PropType<DeepReadonly<DeckUser>>,
    default: () => ({}),
  },
  companyId: {
    type: Number as PropType<number | null>,
    default: null,
  },
  deckId: {
    type: Number as PropType<number | null>,
    default: null,
  },
  uuid: {
    type: String as PropType<string | null>,
    default: null,
  },
  slideRefreshKey: {
    type: Number,
    default: 0,
  },
  animating: {
    type: Boolean,
    default: false,
  },
  threeDViewerError: {
    type: String,
    default: '',
  },
  threeDViewer: {
    type: Object as PropType<UseThreeDViewer>,
    required: true,
  },
  isSidebarOpen: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'frame-change',
  'camera-ready',
  'update:aspect',
  'update:animating',
  'update:load-status',
  'update:transitioning',
])

const { $analytics } = useNuxtApp()
const showErrorModal = ref(false)
const errorModalCustomMessage = ref('')
const layerListExpanded = ref(false)
const container = ref<HTMLElement | null>(null)
const containerResizeObserver = new ResizeObserver(invalidate)
const clock = new Clock()
const controls = getReadOnlyControls()
const progress = computed(() => props.threeDViewer.progress.value)
const { state: userState } = useUser()

const shouldShowLayerList = computed(
  () =>
    userState.value.flags.SEARCHABLE_3D_LAYER_LIST_ENABLED !== false &&
    (props.threeDViewer.layerGroups.value.length || props.threeDViewer.search.value) &&
    progress.value === 1,
)

const layerGroupsWithoutDrillLabels = computed(() =>
  props.threeDViewer.layerGroups.value.filter(group => !isDrillDataSetGroup(group as DrillDataSetLayerGroup)),
)

let frameId: number | null = null
let needsRender = true
let invalidateAndTriggerChange: () => void // will hold a value during mount cycle

useThreeDSceneLoading({
  progress: props.threeDViewer.progress,
  onSceneLoaded: () => emit('camera-ready', true),
})

const { selectedLegends, layerListGroupsWithLegends, toggleLegendVisibility } = useAssayLegends({
  threeDViewer: props.threeDViewer,
})

const numericalAssayLegends = computed(() => selectedLegends.value.filter(l => l.type === 'numerical_assay'))
const categoricalAssayLegends = computed(() => selectedLegends.value.filter(l => l.type === 'categorical_assay'))

watch([() => props.slide, () => props.slideRefreshKey], async ([newSlide], [oldSlide]) => {
  layerListExpanded.value = false

  emit('camera-ready', true)

  if (newSlide.model_id && newSlide.camera_id && (newSlide.model_id !== oldSlide.model_id || newSlide.camera_id !== oldSlide.camera_id)) {
    $analytics.track('showedScreenModelViewer', { modelId: newSlide.model_id, uuid: props.uuid })
  }
})

watch(
  () => props.threeDViewerError,
  message => {
    if (message) {
      showErrorModal.value = true
      errorModalCustomMessage.value = message
    }
  },
  { immediate: true },
)

onMounted(async () => {
  if (!container.value) {
    throw new Error('Failed to mount ThreeDSlide. Container was nullish')
  }
  containerResizeObserver.observe(container.value)
  props.threeDViewer.world.mount(container.value)

  // reported for annotations layer
  emit('update:aspect', 100)

  const debounceFrameChanged = debounce(() => emit('frame-change'), 100, true)
  invalidateAndTriggerChange = () => {
    debounceFrameChanged() // annotations
    invalidate()
  }
  controls.addEventListener(ControlEvent.ViewChangedEvent, invalidateAndTriggerChange)
  controls.addEventListener(ControlEvent.StartTween, startTween)
  controls.addEventListener(ControlEvent.StopTween, stopTween)
  controls.addEventListener(ControlEvent.SetCameraTweenStart, slideTransitionStart)
  controls.addEventListener(ControlEvent.SetCameraTweenComplete, slideTransitionComplete)

  const { model_id, camera_id } = props.slide
  if (model_id && camera_id) {
    $analytics.track('showedScreenModelViewer', { modelId: model_id, uuid: props.uuid })
  }

  _onFrame()
})

onUnmounted(() => {
  if (frameId) {
    cancelAnimationFrame(frameId)
  }

  containerResizeObserver.disconnect()

  controls.removeEventListener(ControlEvent.ViewChangedEvent, invalidateAndTriggerChange)
  controls.removeEventListener(ControlEvent.StartTween, startTween)
  controls.removeEventListener(ControlEvent.StopTween, stopTween)
  controls.removeEventListener(ControlEvent.SetCameraTweenStart, slideTransitionStart)
  controls.removeEventListener(ControlEvent.SetCameraTweenComplete, slideTransitionComplete)

  controls.detach()

  emit('update:transitioning', false)
  emit('update:animating', false)
  emit('update:load-status', false)
})

function invalidate() {
  needsRender = true
}

function startTween() {
  emit('update:animating', true)
}

function stopTween() {
  emit('update:animating', false)
}

function slideTransitionStart() {
  emit('update:transitioning', true)
}

function slideTransitionComplete() {
  emit('update:transitioning', false)
}

function onErrorModalRefresh() {
  $analytics.track('modelViewerErrorRefresh', {
    userId: props.user.id,
    companyId: props.companyId,
    deckId: props.deckId,
  })
  if (process.client) {
    location.reload()
  }
}

function deprecatedOnSetLayerVisible(param: NodeVisibility[]) {
  props.threeDViewer.setLayerVisibility(param)
  invalidate()
}

function _onFrame() {
  frameId = requestAnimationFrame(_onFrame)

  const delta = clock.getDelta()
  const cameraUpdated = controls.update(delta)

  if (props.threeDViewerError) {
    cancelAnimationFrame(frameId!)
    return
  }

  if (!cameraUpdated && !needsRender && !props.threeDViewer.world.needsRender) {
    return
  }

  props.threeDViewer.world.render()

  needsRender = false
}

function onLabelResize(params: { width: number; height: number; label: TextLabel }) {
  props.threeDViewer.onLabelResize(params)
  invalidate()
}

function onSetLayerVisible(param: NodeVisibility[]) {
  if (isDrillLabelVisibility(param)) {
    toggleLegendVisibility({ ...param[0], id: param[0].drillLabelId as string })
  }
  invalidate()
}
</script>

<style lang="stylus" scoped>
:deep(.modal-overlay)
  @apply absolute

.viewer-container
  background radial-gradient(#4a4a4a, #212121)
  .progress-container
    @apply p-1 absolute top-0 left-0 z-50 w-full
  .progress
    @apply h-2 bg-white
    transition-property opacity, width, background-color
    transition-duration 400ms, 500ms, 200ms
    transition-delay 1000ms, 0ms, 0ms
    transition-timing-function ease-out

@css {
  .viewer-container .progress {
    background-color: rgba(255, 255, 255, calc(var(--progress-amount) / 2 + 0.5))
  }
}
</style>
